import {push} from 'connected-react-router';
import alertApi from 'Core/api/alertApi';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {
  Alert,
  AlertWithErrors,
  changeDescription,
  changeEndDate,
  changeStartDate,
  changeTitle,
  EmptyAlert,
  InvalidAlert,
  ValidAlert,
  changeApplications,
} from 'Core/models/Alerts';
import {Async, AsyncPage, AsyncResult, Empty, fromResult, Loaded, Loading, toEither} from 'Core/models/Async';
import {DateRange, EmptyDateRange} from 'Core/models/DateRange';
import {NonEmptyString} from 'Core/models/EmptyString';
import {FilterOptionUnion, FilterOptionValue} from 'Core/models/FilterOption';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import {constant, identity} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {DateTime} from 'luxon';
import {Action} from 'redux';
import {call, put, takeLatest} from 'redux-saga/effects';
import {createAction} from 'Shared/utilities/actionCreator';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {getPageApiCallGenerator, PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {ApplicationEnum, mapLineOfBusinessToApplication} from 'Users/models/Application';
import {AlertMessage} from '../../Core/components/Alerts/AlertsModal';
import {LineOfBusinessValue} from './../../Users/models/LineOfBusiness';

export type AlertFilters = {
  title: string;
  description: string;
  startDate: DateRange;
  endDate: DateRange;
  applicationId: FilterOptionUnion;
};

export enum AlertsActionType {
  GET_ALERTS = 'alerts/GET_ALERTS',
  GET_USER_ALERTS = 'alerts/GET_USER_ALERTS',
  UPDATE_ALERT = 'alerts/UPDATE_ALERT',
  GET_ALERT = 'alerts/GET_ALERT',
  DELETE_ALERT = 'alerts/DELETE_ALERT',
  CHANGE_ALERT = 'alerts/CHANGE_ALERT',
  CHANGE_ALERTS_FILTER = 'alerts/CHANGE_ALERTS_FILTER',
  CHANGE_ALERTS = 'alerts/CHANGE_ALERTS',
  CHANGE_USER_ALERTS = 'alerts/CHANGE_USER_ALERTS',
  LOAD_EXISTING_ALERT = 'alerts/LOAD_EXISTING_ALERT',
  SAVE_ALERT = 'alerts/SAVE_ALERT',
  CLEAR_ALERT = 'alerts/CLEAR_ALERT',
}

export const EmptyAlertFilter: AlertFilters = {
  description: '',
  endDate: EmptyDateRange,
  startDate: EmptyDateRange,
  title: '',
  applicationId: FilterOptionValue.EmptyOption(),
};

export type AlertPayload = {
  id: string | undefined;
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  applications?: ApplicationEnum[];
  applicationIds?: number[];
};

export const getAlert = (alertId: string) => createAction(AlertsActionType.GET_ALERT, {alertId});

export const getUserAlerts = () => {
  const payload = pipe(
    PageFetchAction.Initial({
      filter: {
        ...EmptyAlertFilter,
        endDate: {
          endDate: '',
          startDate: new Date(),
        },
        startDate: {
          endDate: new Date(),
          startDate: '',
        },
      },
    })
  );

  return createAction(AlertsActionType.GET_USER_ALERTS, payload);
};
export const getAlerts = (pageOption?: Page<AlertPayload>, filter?: AlertFilters) => {
  const applicationId =
    filter?.applicationId.tag === 'SelectedOption'
      ? mapLineOfBusinessToApplication(filter?.applicationId.value as LineOfBusinessValue)
      : 0;

  const payload = pipe(
    O.fromNullable(pageOption),
    O.fold(
      constant(
        PageFetchAction.Initial({
          filter: {
            ...filter,
            applicationId,
          },
        })
      ),
      page =>
        PageFetchAction.Additional({
          existingPage: page,
          filter: {
            ...filter,
            applicationId,
          },
        })
    )
  );

  return createAction(AlertsActionType.GET_ALERTS, payload);
};

export const changeAlertTitle = (title: string) =>
  createAction(AlertsActionType.CHANGE_ALERT, {getAlert: changeTitle(title)});
export const changeAlertDescription = (description: string) =>
  createAction(AlertsActionType.CHANGE_ALERT, {getAlert: changeDescription(description)});
export const changeAlertStartDate = (startDate: DateTime | null) =>
  createAction(AlertsActionType.CHANGE_ALERT, {getAlert: changeStartDate(startDate)});
export const changeAlertEndDate = (endDate: DateTime | null) =>
  createAction(AlertsActionType.CHANGE_ALERT, {getAlert: changeEndDate(endDate)});
export const changeAlertApplications = (applications: ApplicationEnum[]) =>
  createAction(AlertsActionType.CHANGE_ALERT, {getAlert: changeApplications(applications)});
export const loadAlert = (alert: Async<Alert>) => createAction(AlertsActionType.LOAD_EXISTING_ALERT, {alert});
export const changeAlertsFilter = (alertFilter: AlertFilters) =>
  createAction(AlertsActionType.CHANGE_ALERTS_FILTER, alertFilter);
export const clearAlertsFilter = () => createAction(AlertsActionType.CHANGE_ALERTS_FILTER, DEFAULT_STATE.alertFilters);
export const saveAlert = (alert: ValidAlert) => createAction(AlertsActionType.SAVE_ALERT, alert);
export const deleteAlert = (id: string) => createAction(AlertsActionType.DELETE_ALERT, id);
export const changeUserAlerts = (alerts: AsyncPage<Page<AlertPayload>>) =>
  createAction(AlertsActionType.CHANGE_USER_ALERTS, alerts);
export const changeAlerts = (alerts: AsyncPage<Page<AlertPayload>>) =>
  createAction(AlertsActionType.CHANGE_ALERTS, alerts);
export const clearAlert = () => createAction(AlertsActionType.CLEAR_ALERT);

type AlertState = {
  alerts: AsyncPage<Page<AlertPayload>>;
  userAlerts: AsyncPage<Page<AlertPayload>>;
  alertFilters: AlertFilters;
  alert: Async<Alert>;
};

export const DEFAULT_STATE: AlertState = {
  alert: new Loaded(EmptyAlert),
  userAlerts: new Empty(),
  alertFilters: EmptyAlertFilter,
  alerts: new Empty(),
};

type AlertAction =
  | ReturnType<typeof getAlert>
  | ReturnType<typeof changeAlertTitle>
  | ReturnType<typeof changeAlertDescription>
  | ReturnType<typeof changeUserAlerts>
  | ReturnType<typeof changeAlerts>
  | ReturnType<typeof loadAlert>
  | ReturnType<typeof saveAlert>
  | ReturnType<typeof changeAlertsFilter>
  | ReturnType<typeof clearAlert>;

export default (state: AlertState = DEFAULT_STATE, action: AlertAction): AlertState => {
  switch (action.type) {
    case AlertsActionType.SAVE_ALERT:
    case AlertsActionType.GET_ALERT:
      return {
        ...state,
        alert: new Loading(),
      };
    case AlertsActionType.LOAD_EXISTING_ALERT:
      return {
        ...state,
        alert: action.payload.alert,
      };
    case AlertsActionType.CHANGE_USER_ALERTS:
      return {
        ...state,
        userAlerts: action.payload,
      };
    case AlertsActionType.CLEAR_ALERT:
      return {
        ...state,
        alert: DEFAULT_STATE.alert,
      };

    case AlertsActionType.CHANGE_ALERTS_FILTER:
      return {
        ...state,
        alertFilters: action.payload,
      };
    case AlertsActionType.CHANGE_ALERT:
      return pipe(
        state.alert,
        toEither,
        E.fold(
          () => state,
          loadedAlert => ({
            ...state,
            alert: fromResult(action.payload.getAlert(loadedAlert)),
          })
        )
      );
    case AlertsActionType.CHANGE_ALERTS:
      return {
        ...state,
        alerts: action.payload,
      };
    default:
      return state;
  }
};

export function* saveAlertRequest(action: ReturnType<typeof saveAlert>) {
  const isEdit = O.isSome(action.payload.id);
  const alertToSave: AlertPayload = {
    id: pipe(
      action.payload.id,
      O.getOrElse((): string | undefined => undefined)
    ),
    title: action.payload.title.value,
    description: action.payload.description.value,
    startDate: action.payload.startDate.value.toJSDate(),
    endDate: action.payload.endDate.value.toJSDate(),
    applicationIds: action.payload.applications.map(a => a.id) as number[],
  };

  const savedAlert: AsyncResult<AlertMessage> = isEdit
    ? yield call(alertApi.updateAlert, alertToSave)
    : yield call(alertApi.saveAlert, alertToSave);

  yield pipe(
    savedAlert,
    E.map(() => put(push(CoreRouteConstants.ALERTS))),
    E.mapLeft(errors => loadAlert(fromResult(AlertWithErrors.fromAlert(action.payload, errors.message)))),
    E.fold(a => a as Action, identity)
  );
}

export function* saveAlertSaga() {
  yield takeLatest(AlertsActionType.SAVE_ALERT, saveAlertRequest);
}

export function* deleteAlertRequest(action: ReturnType<typeof deleteAlert>) {
  yield put(changeAlerts(new Loading()));

  yield call(alertApi.deleteAlert, action.payload);

  yield put(getAlerts());
}

export function* deleteAlertSaga() {
  yield takeLatest(AlertsActionType.DELETE_ALERT, deleteAlertRequest);
}

export function* getAlertFromApi(action: ReturnType<typeof getAlert>) {
  const result: AsyncResult<AlertPayload> = yield call(alertApi.getAlert, action.payload.alertId);

  yield pipe(
    result,
    E.map(value => new Loaded(value)),
    E.fold(
      value => put(loadAlert(value)),
      value =>
        put(
          loadAlert(
            new Loaded(
              ValidAlert.create(
                new InvalidAlert(
                  O.fromNullable(value.Data.id || ''),
                  NonEmptyString.create(value.Data.title),
                  NonEmptyString.create(value.Data.description),
                  O.some(DateTime.fromJSDate(new Date(value.Data.startDate))),
                  O.some(DateTime.fromJSDate(new Date(value.Data.endDate))),
                  value.Data.applications || []
                )
              )
            )
          )
        )
    )
  );
}

export function* getAlertSaga() {
  yield takeLatest(AlertsActionType.GET_ALERT, getAlertFromApi);
}

export const getUserAlertsFromApi = (action: ReturnType<typeof getUserAlerts>) =>
  PageFetchAction.match({
    Additional: additionalPage => mergeWithNextPage<AlertPayload>(additionalPage),
    Initial: () => alertApi.getAlerts(action.payload.filter),
  })(action.payload);

export function* getUserAlertsSaga() {
  yield takeLatest(AlertsActionType.GET_USER_ALERTS, getPageApiCallGenerator(getUserAlertsFromApi, changeUserAlerts));
}

export const getAlertsFromApi = (action: ReturnType<typeof getAlerts>) =>
  PageFetchAction.match({
    Additional: additionalPage => mergeWithNextPage<AlertPayload>(additionalPage),
    Initial: () => alertApi.getAlerts(action.payload.filter),
  })(action.payload);

export function* getAlertsSaga() {
  yield takeLatest(AlertsActionType.GET_ALERTS, getPageApiCallGenerator(getAlertsFromApi, changeAlerts));
}
